import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMaintenanceMode } from "../context/MaintenanceContext";
import {
  Skeleton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Typography,
  InputAdornment,
  TextField,
} from "@mui/material";
import MaintenancePopup from "./MaintenancePopup";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from '@mui/material/styles';


const HighlightedText = ({ text, searchTerm }) => {
  const theme = useTheme();
  if (!searchTerm) return <span>{text}</span>;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <span>
      {parts.map((part, index) => 
        regex.test(part) ? (
          <mark key={index} style={{ 
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.2)' 
              : 'rgba(128, 128, 128, 0.3)',
            padding: '2px 0',
            fontWeight: 500,
            color: 'inherit' 
          }}>
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
};

const ProjectSelection = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [organization, setOrganization] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const theme = useTheme();
  const { isPageInMaintenance } = useMaintenanceMode();

  const navigate = useNavigate();
  const organizationId = localStorage.getItem("organization_id");
  const token = localStorage.getItem("access_token");

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects?organization_id=${organizationId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setProjects(data.projects_list || []);
        setFilteredProjects(data.projects_list || []);
        setOrganization(data.organization_name);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setIsLoading(false);
      });
  }, []);

  const handleProjectClick = (project) => {
    navigate(`/projects/${project.project_id}`);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filtered = projects.filter((project) =>
      project.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredProjects(filtered);
    setPage(0);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.mode === "dark" ? "#282828" : "background.default",
        color: "text.primary",
        p: 0,
        m: 0,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          filter: isPageInMaintenance('projects') ? 'blur(4px)' : 'none',
          transition: 'filter 0.3s ease',
          pointerEvents: isPageInMaintenance('projects') ? 'none' : 'auto',
        }}
      >
        <Box sx={{ml:2, mr:2}}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h5" component="h2" sx={{ mt: 2, ml: 2 }}>
              All {organization} Projects
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 2, mr: 2 }}>
              <TextField
                variant="outlined"
                placeholder="Search Projects"
                value={searchTerm}
                onChange={handleSearch}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                className="athrv-dash-primary-button"
                onClick={() => navigate("/projects/create")}
                sx={{
                  color: theme.palette.mode === "dark" ? "#fff" : "#fff",
                }}
              >
                Create New Project
              </Button>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="text" width="60%" height={30} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
            </Box>
          ) : filteredProjects.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ marginTop: "20px" }}>
              No projects found. Try a different search or create a new project.
            </Typography>
          ) : (
            <Box sx={{ pb: 2}}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark' ? '#202020' : 'rgb(12, 20, 50)',
                          '& .MuiTableCell-root': {
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            borderBottom: 'none',
                          }
                        }}
                      >
                      <TableCell>Project Name</TableCell>
                      <TableCell align="right">Description</TableCell>
                      <TableCell align="right">Last Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProjects
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((project) => (
                        <TableRow
                            key={project.project_id}
                            onClick={() => handleProjectClick(project)}
                            sx={{
                              cursor: "pointer",
                              backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#fff',
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? '#333333' : '#f5f5f5',
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid #1a1a1a' : '1px solid rgba(224, 224, 224, 1)',
                              }
                            }}
                          >
                          <TableCell component="th" scope="row">
                            <HighlightedText
                              text={project.name}
                              searchTerm={searchTerm}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <HighlightedText
                              text={project.description}
                              searchTerm={searchTerm}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {new Date(
                              parseInt(project.createdAt) * 1000
                            ).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 15, 20]}
                  component="div"
                  count={filteredProjects.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>

      <MaintenancePopup open={isPageInMaintenance('projects')} />

    </Box>
  );
};

export default ProjectSelection;